/**
 * Operator cross-tenant Users directory — every operator and hotel user account
 * (gated by `admin:cross_tenant_read`). Renders ONLY canonical operator data
 * from `@hostesa/api-client`.
 *
 * Honesty/PII guarantees baked into this surface:
 *  - The payload carries the REAL email; it is MASKED by default in the table
 *    (e.g. `n••••@host.ai`) with a per-row "Reveal" toggle that reveals the
 *    already-loaded value client-side (no extra fetch). A footer note tells the
 *    operator emails are masked by default.
 *  - STATUS is a REAL `users.status` field, so the column is shown.
 *  - MFA is a REAL `users.mfa_enabled` field, shown as an On/Off pill.
 *  - There are NO last-seen / phone fields server-side, so those columns are
 *    deliberately OMITTED (nothing is fabricated).
 *  - Operators (`hostesa_admin`) are intentionally not partner-scoped, so their
 *    scope reads "Hostesa internal" rather than a tenant name.
 */

import {
  type OperatorUser,
  type OperatorUserStatus,
  type OperatorUsersFilters,
  type Role,
  useOperatorUsers,
} from '@hostesa/api-client';
import { useT } from '@hostesa/i18n';
import { Button, Card, cn, type PillTone, StatusPill, StatusTabs } from '@hostesa/ui';
import { Users } from 'lucide-react';
import { type KeyboardEvent, useState } from 'react';
import { OversightFrame } from './list-scaffold';

const USER_STATUSES: readonly OperatorUserStatus[] = ['invited', 'active', 'disabled'];
const USER_STATUS_SET = new Set<string>(USER_STATUSES);

/** Tab id → backend `type` filter. `operators`/`hotel` map to operator/hotel. */
type UsersTab = 'all' | 'operators' | 'hotel';

function statusTone(status: OperatorUserStatus): PillTone {
  if (status === 'active') return 'success';
  if (status === 'disabled') return 'alert';
  return 'neutral';
}

/**
 * Mask an email for display: keep the first character of the local part, replace
 * the rest of the local part with bullets, keep the domain. Falls back to a
 * generic mask if the address has no `@`.
 */
function maskEmail(email: string): string {
  const at = email.indexOf('@');
  if (at <= 0) return '••••';
  const first = email[0];
  const domain = email.slice(at);
  return `${first}••••${domain}`;
}

function roleKey(role: Role | null): string {
  return role ?? 'none';
}

// ───────────────────────────────────────────────────────────────────────────

export interface UsersPageProps {
  filters?: OperatorUsersFilters;
  onFiltersChange?: (next: OperatorUsersFilters) => void;
  /** Optional click handler for a user row (router wires navigation). */
  onSelectUser?: (userId: string) => void;
}

export function UsersPage({ filters = {}, onFiltersChange, onSelectUser }: UsersPageProps = {}) {
  const { t } = useT('backoffice');
  const apply = onFiltersChange ?? (() => {});
  const limit = filters.limit ?? 50;
  const offset = filters.offset ?? 0;

  const query = useOperatorUsers({ ...filters, limit, offset });
  const rows = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const filterActive = Boolean(filters.q || filters.status || filters.type);
  const patch = (next: Partial<OperatorUsersFilters>) => apply({ ...filters, ...next, offset: 0 });

  // Header metrics derive from the loaded page rows (operators = hostesa_admin
  // membership; everyone else is a hotel user). These are page-scoped counts.
  const operatorsCount = rows.filter((u) => u.role === 'hostesa_admin').length;
  const hotelUsersCount = rows.length - operatorsCount;

  const activeTab: UsersTab =
    filters.type === 'operator' ? 'operators' : filters.type === 'hotel' ? 'hotel' : 'all';

  return (
    <OversightFrame
      testId="users-page"
      eyebrow={t('navGroups.customers')}
      title={t('pages.users.title')}
      description={t('pages.users.description')}
      headerTrailing={
        <div className="flex shrink-0 gap-6" data-testid="users-page-metrics">
          <HeaderMetric
            testId="users-metric-operators"
            label={t('oversight.users.metrics.operators')}
            value={operatorsCount}
          />
          <HeaderMetric
            testId="users-metric-hotel"
            label={t('oversight.users.metrics.hotelUsers')}
            value={hotelUsersCount}
          />
        </div>
      }
      searchValue={filters.q ?? ''}
      onSearchChange={(v) => patch({ q: v || undefined })}
      searchPlaceholder={t('oversight.users.searchPlaceholder')}
      filterActive={filterActive}
      onClear={() => apply({ limit })}
      isLoading={query.isLoading}
      isError={query.isError}
      isEmpty={rows.length === 0}
      emptyIcon={<Users />}
      emptyTitle={t('oversight.users.empty.title')}
      emptyDescription={t('oversight.users.empty.description')}
      emptyFilteredTitle={t('oversight.users.emptyFiltered.title')}
      emptyFilteredDescription={t('oversight.users.emptyFiltered.description')}
      total={total}
      limit={limit}
      offset={offset}
      onPageChange={({ offset: o, limit: l }) => apply({ ...filters, offset: o, limit: l })}
    >
      <StatusTabs
        data-testid="users-tab"
        value={activeTab}
        onChange={(id) =>
          patch({
            type: id === 'operators' ? 'operator' : id === 'hotel' ? 'hotel' : undefined,
          })
        }
        tabs={[
          { id: 'all', label: t('oversight.users.tabs.all') },
          { id: 'operators', label: t('oversight.users.tabs.operators') },
          { id: 'hotel', label: t('oversight.users.tabs.hotel') },
        ]}
        className="mb-3"
      />
      <UsersTable rows={rows} onSelectUser={onSelectUser} />
      <p
        data-testid="users-masked-note"
        className="mt-3 font-[var(--font-mono)] text-[11px] text-[var(--color-stone-mid)]"
      >
        {t('oversight.users.maskedNote')}
      </p>
    </OversightFrame>
  );
}

// ───────────────────────────────────────────────────────────────────────────

function HeaderMetric({ testId, label, value }: { testId: string; label: string; value: number }) {
  return (
    <div className="flex flex-col gap-1" data-testid={testId}>
      <span className="font-[var(--font-mono)] text-[10px] font-medium uppercase tracking-widest text-[var(--color-stone-mid)]">
        {label}
      </span>
      <span
        data-testid={`${testId}-value`}
        className="font-[var(--font-display)] text-2xl leading-none text-[var(--color-charcoal)]"
      >
        {value}
      </span>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// The table is split out so each row owns its own "reveal email" toggle state.
// ───────────────────────────────────────────────────────────────────────────

function UsersTable({
  rows,
  onSelectUser,
}: {
  rows: OperatorUser[];
  onSelectUser?: (userId: string) => void;
}) {
  const { t } = useT('backoffice');
  // Track which rows have their real email revealed (already-loaded data only).
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});

  return (
    <Card data-testid="users-table" className="overflow-hidden">
      <div className="hidden grid-cols-[minmax(0,2fr)_minmax(0,2fr)_minmax(0,1.5fr)_90px_120px] gap-4 border-b border-[var(--color-line)] px-4 py-2 font-[var(--font-mono)] text-[10px] uppercase tracking-widest text-[var(--color-stone-mid)] sm:grid">
        <span>{t('oversight.users.columns.user')}</span>
        <span>{t('oversight.users.columns.email')}</span>
        <span>{t('oversight.users.columns.scope')}</span>
        <span>{t('oversight.columns.mfa')}</span>
        <span>{t('oversight.users.columns.status')}</span>
      </div>
      <ul className="divide-y divide-[var(--color-line-2)]">
        {rows.map((u) => {
          const isRevealed = Boolean(revealed[u.userId]);
          const scope =
            u.role === 'hostesa_admin'
              ? t('oversight.users.internalScope')
              : (u.tenantName ?? t('oversight.users.unknownScope'));
          return (
            <li
              key={u.userId}
              data-testid={`users-table-row-${u.userId}`}
              {...(onSelectUser
                ? {
                    role: 'button' as const,
                    tabIndex: 0,
                    onClick: () => onSelectUser(u.userId),
                    onKeyDown: (e: KeyboardEvent) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onSelectUser(u.userId);
                      }
                    },
                  }
                : {})}
              className={cn(
                'grid grid-cols-1 gap-2 px-4 py-3 sm:grid-cols-[minmax(0,2fr)_minmax(0,2fr)_minmax(0,1.5fr)_90px_120px] sm:items-center sm:gap-4',
                onSelectUser &&
                  'cursor-pointer transition-colors hover:bg-[var(--color-line-2)] focus-visible:outline-none focus-visible:bg-[var(--color-line-2)]',
              )}
            >
              {/* USER: name bold + "U-id · role" mono sub */}
              <div className="flex min-w-0 flex-col gap-0.5">
                <span className="truncate font-medium text-[var(--color-charcoal)]">
                  {u.fullName ?? u.email}
                </span>
                <span className="truncate font-[var(--font-mono)] text-[11px] text-[var(--color-stone-mid)]">
                  U-{u.userId.slice(0, 8)} · {t(`oversight.users.roles.${roleKey(u.role)}`)}
                </span>
              </div>

              {/* EMAIL: masked by default with a per-row reveal toggle */}
              <div className="flex min-w-0 items-center gap-2">
                <span
                  data-testid={`users-email-${u.userId}`}
                  className="truncate font-[var(--font-mono)] text-[12.5px] text-[var(--color-stone-700)]"
                >
                  {isRevealed ? u.email : maskEmail(u.email)}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  data-testid={`users-reveal-${u.userId}`}
                  aria-pressed={isRevealed}
                  onClick={(e) => {
                    e.stopPropagation();
                    setRevealed((prev) => ({ ...prev, [u.userId]: !prev[u.userId] }));
                  }}
                >
                  {isRevealed ? t('oversight.users.hide') : t('oversight.users.reveal')}
                </Button>
              </div>

              {/* SCOPE: tenant name, or "Hostesa internal" for operators */}
              <span className="truncate text-[13.5px] text-[var(--color-stone-700)]">{scope}</span>

              {/* MFA: real `users.mfa_enabled` field → On/Off pill (— if an older
                  API hasn't shipped the field yet, for deploy-skew tolerance) */}
              <div>
                {u.mfaEnabled === undefined ? (
                  <span className="text-[var(--color-stone-mid)]">
                    {t('oversight.notAvailable')}
                  </span>
                ) : (
                  <StatusPill
                    dot
                    data-testid={`users-mfa-${u.userId}`}
                    tone={u.mfaEnabled ? 'success' : 'neutral'}
                    label={
                      u.mfaEnabled ? t('oversight.users.mfa.on') : t('oversight.users.mfa.off')
                    }
                  />
                )}
              </div>

              {/* STATUS: real field → StatusPill */}
              <div>
                <StatusPill
                  dot
                  tone={statusTone(u.status)}
                  label={t(`oversight.users.status.${u.status}`)}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// URL search parsing (router validateSearch). Only provided, valid params are
// kept — defaults (limit 50 / offset 0) are owned by the page + backend.
// ───────────────────────────────────────────────────────────────────────────

const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : undefined);
const num = (v: unknown) => {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : Number.NaN;
  return Number.isFinite(n) ? n : undefined;
};

export function parseUsersSearch(raw: Record<string, unknown>): OperatorUsersFilters {
  const out: OperatorUsersFilters = {};
  if (str(raw.q)) out.q = str(raw.q);
  const status = str(raw.status);
  if (status && USER_STATUS_SET.has(status)) out.status = status as OperatorUserStatus;
  const type = str(raw.type);
  if (type === 'operator' || type === 'hotel') out.type = type;
  const limit = num(raw.limit);
  const offset = num(raw.offset);
  if (limit !== undefined) out.limit = Math.min(Math.max(limit, 1), 200);
  if (offset !== undefined && offset > 0) out.offset = offset;
  return out;
}
